import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { useCreateCidadaoCadsusMutation, useSincronizaCidadaoCadsusPollingQuery } from 'graphql/hooks.generated'
import { CidadaoCadsus } from 'graphql/types.generated'
import useAtmosphere from 'hooks/useAtmosphere'
import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ResultSyncCadsusType } from 'view/cidadao/create/cadsus/types/model-cadsus'
import { useNeedLoginGovBrToCadsus } from 'view/cidadao/hooks/useNeedLoginGovBrToCadsus'

import { InfosExcluiveisCadsus } from './useCadsusEditView'

export interface CadsusAtmosphereResponse {
  uuid: string
}

let pollingCadsus = false

const POLLING_INTERVAL_TIME_1S = 1 * 1000 // 1 segundo

export function useCadsusCreateView() {
  const cidadaoCadsusSessionStorage = JSON.parse(sessionStorage.getItem('cidadaoCadsus')) as CidadaoCadsus
  const { needLoginGovBr, hasAllConfigs } = useNeedLoginGovBrToCadsus()
  const { data: sessao } = useSession()
  const { refetch: refetchSincronizacaoPolling } = useSincronizaCidadaoCadsusPollingQuery({ skip: true })
  const handleRejection = useErrorHandler()

  const [saveCadsus] = useCreateCidadaoCadsusMutation()
  const [syncCadsusState, setSyncCadsusState] = useState<boolean>(false)
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false)
  const [cadsusSyncResult, setCadsusSyncResult] = useState<ResultSyncCadsusType>({ uuid: '' })
  const [uuidSincronizacao, setUuidSincronizacao] = useState<string>()
  const [dadosExcluiveisState] = useState<InfosExcluiveisCadsus>({
    telefoneCelular: cidadaoCadsusSessionStorage?.telefoneCelular[0],
    telefoneContato: cidadaoCadsusSessionStorage?.telefoneContato[0],
    telefoneResidencial: cidadaoCadsusSessionStorage?.telefoneResidencial[0],
    nomeSocial: cidadaoCadsusSessionStorage?.nomeSocial,
  })
  const [hasCnsState, setHasCnsState] = useState(false)
  const [isCadsusHabilitado, setIsCadsusHabilitado] = useState<boolean>(false)

  const atualizaResultadoCadsus = (data: ResultSyncCadsusType) => {
    pollingCadsus = false
    setCadsusSyncResult(data)
    setIsModalLoading(false)
  }

  useEffect(() => {
    if (hasAllConfigs) {
      setIsCadsusHabilitado(true)
    }
  }, [hasAllConfigs])

  useEffect(() => {
    if (!isCadsusHabilitado) return

    let shouldUpdate = true
    const scheduleCadsusPolling = setInterval(() => {
      if (!!uuidSincronizacao && pollingCadsus && shouldUpdate) {
        shouldUpdate = false
        refetchSincronizacaoPolling({ uuid: uuidSincronizacao })
          .then(
            ({ data }) =>
              !!data.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidSincronizacao &&
              pollingCadsus &&
              atualizaResultadoCadsus(data.sincronizacaoCadsusPolling)
          )
          .finally(() => (shouldUpdate = true))
          .catch(handleRejection)
      }
    }, POLLING_INTERVAL_TIME_1S)
    return () => clearInterval(scheduleCadsusPolling)
  }, [handleRejection, isCadsusHabilitado, refetchSincronizacaoPolling, uuidSincronizacao])

  const syncCadsus = (cidadaoId: string, cns: string) => {
    if (!isCadsusHabilitado) return

    setCadsusSyncResult(null)
    const hash = uuidv4()
    setUuidSincronizacao(hash)
    pollingCadsus = true
    saveCadsus({
      variables: {
        input: {
          cidadao: cidadaoId,
          telefoneCelularAntigo: dadosExcluiveisState.telefoneCelular,
          telefoneContatoAntigo: dadosExcluiveisState.telefoneContato,
          telefoneResidencialAntigo: dadosExcluiveisState.telefoneResidencial,
          nomeSocialAntigo: dadosExcluiveisState.nomeSocial,
          uuid: hash,
        },
      },
    })

    setIsModalLoading(true)
    setSyncCadsusState(true)
    setHasCnsState(!!cns)
  }

  useAtmosphere<CadsusAtmosphereResponse>({
    topic: `cadsus-sincronizacao-completa/${sessao.profissional.usuario.id}`,
    onMessage: (responseBody) => {
      if (responseBody.uuid === uuidSincronizacao && pollingCadsus) {
        refetchSincronizacaoPolling({ uuid: uuidSincronizacao })
          .then(
            ({ data }) =>
              !!data.sincronizacaoCadsusPolling &&
              data.sincronizacaoCadsusPolling.uuid === uuidSincronizacao &&
              pollingCadsus &&
              atualizaResultadoCadsus(data.sincronizacaoCadsusPolling)
          )
          .catch(handleRejection)
      }
    },
  })

  return {
    cidadaoCadsusSessionStorage,
    syncCadsus,
    needLoginGovBr,
    isCadsusHabilitado,
    isModalLoading,
    cadsusSyncResult,
    syncCadsusState,
    setSyncCadsusState,
    hasCnsState,
  }
}
